import { getUserData } from '../constants/global';
import { type ApiResult, isRetryable, resultMessage } from './api-result';
import { isStoragePaused, RELAY_CODES, runRelayUpload, sessionUsable } from './media-relay';
import {
	type ChunkOutcome,
	type CompleteOutcome,
	completeUpload,
	getUploadSession,
	type IRegisterVideoRequest,
	type IRelaySession,
	type IRelaySessionState,
	type IResultVideo,
	type ISignVideoRequest,
	registerVideo,
	RESULT_VIDEO_CODES,
	signVideo,
	uploadChunk,
} from './result-video-api';
import { deleteVideoBlob, getVideoBlob, putVideoBlob } from './result-video-media';
import {
	getVideoRecord,
	type IResultVideoRecord,
	listVideoRecords,
	newVideoRecord,
	notifyVideoChanged,
	updateVideoRecord,
	uploadedPercent,
	writeVideoRecord,
} from './result-video-store';
import { hasExhaustedAttempts, MAX_ATTEMPTS, nextAttemptAt } from './support-backoff';

export interface IVideoSyncDeps {
	sign: (electionId: number, body: ISignVideoRequest) => Promise<ApiResult<IRelaySession>>;
	getSession: (electionId: number, uploadId: string) => Promise<ApiResult<IRelaySessionState>>;
	uploadChunk: (
		electionId: number,
		session: IRelaySession,
		blob: Blob,
		index: number,
		onProgress?: (loaded: number) => void
	) => Promise<ChunkOutcome>;
	complete: (electionId: number, uploadId: string) => Promise<CompleteOutcome>;
	register: (electionId: number, body: IRegisterVideoRequest) => Promise<ApiResult<IResultVideo>>;
	getBlob: (id: string) => Promise<Blob | null>;
	deleteBlob: (id: string) => Promise<void>;
	/** True when uploads must wait (the agent chose Wi-Fi only and the phone is on cellular). */
	onMeteredConnection: () => boolean;
	now: () => number;
	random: () => number;
}

const meteredConnection = (): boolean => {
	try {
		const connection = (navigator as Navigator & { connection?: { type?: string; saveData?: boolean } }).connection;
		return connection?.type === 'cellular';
	} catch {
		return false;
	}
};

export const defaultVideoSyncDeps: IVideoSyncDeps = {
	sign: signVideo,
	getSession: getUploadSession,
	uploadChunk,
	complete: completeUpload,
	register: registerVideo,
	getBlob: getVideoBlob,
	deleteBlob: deleteVideoBlob,
	onMeteredConnection: meteredConnection,
	now: () => Date.now(),
	random: Math.random,
};

// ---------------------------------------------------------------------------
// Microcopy — sentence case, plain verbs, no apology
// ---------------------------------------------------------------------------

export const VIDEO_COPY = {
	windowClosedReviewed: 'Your result was reviewed, so its video can no longer be changed. Your result is unaffected.',
	windowClosedExpired: 'The time to add a video for this result has passed. Your result is unaffected.',
	off: 'Result videos are turned off for this election. Your result is unaffected.',
	notAssigned: 'This polling unit isn’t assigned to you, so its video can’t be added.',
	refused: 'The server refused this video.',
	missing: 'This video is no longer on this phone. Record it again.',
	held: 'Upload paused after several tries. Tap Retry when you have signal.',
	storagePaused: 'Uploads are paused on the server. Your recording is saved on this phone and will upload automatically.',
	mediaRejected: 'The server couldn’t accept this file. Record the video again.',
};

/**
 * Copy for a refusal. A server `message` is shown only for an api.core refusal with no code we
 * know — never for a storage code, whose text can be the provider's own (e.g. "cloud_name is
 * disabled").
 */
const refusalMessage = (status: number, code?: string, message?: string): string => {
	if (isStoragePaused(code)) return VIDEO_COPY.storagePaused;
	if (code === RELAY_CODES.mediaRejected) return VIDEO_COPY.mediaRejected;
	if (code === RESULT_VIDEO_CODES.windowClosed) {
		return message?.toLowerCase().includes('review') ? VIDEO_COPY.windowClosedReviewed : VIDEO_COPY.windowClosedExpired;
	}
	if (code === RESULT_VIDEO_CODES.off) return VIDEO_COPY.off;
	if (status === 403) return VIDEO_COPY.notAssigned;
	return message || VIDEO_COPY.refused;
};

/** A response that cannot succeed if sent again. 401 is not one: the agent can sign back in. */
const isTerminal = (r: ApiResult<unknown>): r is Extract<ApiResult<unknown>, { kind: 'rejected' | 'conflict' }> =>
	(r.kind === 'rejected' && r.status !== 401) ||
	(r.kind === 'conflict' &&
		(r.code === RESULT_VIDEO_CODES.windowClosed ||
			r.code === RESULT_VIDEO_CODES.off ||
			r.code === RESULT_VIDEO_CODES.belongsElsewhere));

/**
 * One more failed attempt. Storage being paused never exhausts the ladder: the server said it
 * will take the file later, so the record keeps retrying on its own (at the ladder's ceiling)
 * instead of waiting for a tap. `retryAfterMs` (a 429's `Retry-After`) is a floor on the delay.
 */
const backoff = (
	r: IResultVideoRecord,
	deps: IVideoSyncDeps,
	{ error, code, retryAfterMs }: { error?: string; code?: string; retryAfterMs?: number } = {}
): Partial<IResultVideoRecord> => {
	const paused = isStoragePaused(code);
	const attempts = paused ? Math.min(r.attempts + 1, MAX_ATTEMPTS - 1) : r.attempts + 1;
	if (!paused && hasExhaustedAttempts(attempts)) return { attempts, state: 'held', error: error ?? VIDEO_COPY.held, code };
	const now = deps.now();
	const at = Math.max(nextAttemptAt(attempts, now, deps.random), retryAfterMs ? now + retryAfterMs : 0);
	return { attempts, nextAttemptAt: at, error: paused ? VIDEO_COPY.storagePaused : error, code };
};

export interface IVideoSyncOptions {
	/** Ignore backoff, Wi-Fi-only and held (a manual Retry). */
	force?: boolean;
	deps?: IVideoSyncDeps;
	/** Overall upload progress 0–100. */
	onProgress?: (percent: number) => void;
}

const inflight = new Map<string, Promise<IResultVideoRecord | null>>();

export const isVideoSyncing = (electionId: number, puCode: string) => inflight.has(`${electionId}:${puCode}`);

/** One run per unit at a time in this tab; a second caller joins the first. */
export const syncResultVideo = (
	electionId: number,
	puCode: string,
	options: IVideoSyncOptions = {}
): Promise<IResultVideoRecord | null> => {
	const key = `${electionId}:${puCode}`;
	const running = inflight.get(key);
	if (running) return running;
	const run = runSync(electionId, puCode, options).finally(() => {
		inflight.delete(key);
		// The last record write happened while this run still counted as in flight; tell the
		// surfaces it has ended, or a paused upload keeps reading "Uploading".
		notifyVideoChanged(electionId, puCode);
	});
	inflight.set(key, run);
	notifyVideoChanged(electionId, puCode);
	return run;
};

const runSync = async (
	electionId: number,
	puCode: string,
	{ force = false, deps = defaultVideoSyncDeps, onProgress }: IVideoSyncOptions
): Promise<IResultVideoRecord | null> => {
	let record = getVideoRecord(electionId, puCode);
	if (!record) return null;
	if (record.state === 'registered' || record.state === 'refused' || record.state === 'missing') return record;
	if (record.state === 'held' && !force) return record;
	if (!force && record.nextAttemptAt > deps.now()) return record;
	if (!force && record.wifiOnly && deps.onMeteredConnection()) return record;

	const id = record.id;
	const patch = (p: Partial<IResultVideoRecord>) => {
		record = updateVideoRecord(electionId, puCode, (r) => (r.id === id ? { ...r, ...p } : r));
		return record;
	};

	// ── 1. bytes → api.core relay, chunk by chunk ──
	if (record.state !== 'uploaded' || !record.storagePublicId) {
		const blob = await deps.getBlob(id);
		if (!blob) return patch({ state: 'missing', error: VIDEO_COPY.missing });

		const sign = async () =>
			deps.sign(electionId, {
				pollingUnitCode: record!.pollingUnitCode,
				mime: record!.mime,
				bytes: blob.size,
				durationSec: record!.durationSec,
				clientUploadId: record!.clientUploadId,
				fileName: record!.fileName,
				sha256: record!.sha256,
			});

		// A stored session is reused only while api.core says it is live; expiry comes from the
		// server's `expiresAt`. A record without one (new, or migrated from Cloudinary) signs.
		let session = sessionUsable(record.session, deps.now()) ? record.session! : undefined;
		if (!session) {
			const res = await sign();
			if (res.kind !== 'ok') {
				if (isTerminal(res)) {
					return patch({ state: 'refused', code: res.code, error: refusalMessage(res.status, res.code, resultMessage(res)) });
				}
				const code = 'code' in res ? res.code : undefined;
				return patch({
					state: 'queued',
					session: undefined,
					...backoff(record, deps, { code, error: isRetryable(res) || isStoragePaused(code) ? undefined : resultMessage(res) }),
				});
			}
			session = res.data;
			patch({ session, windowClosesAt: res.data.windowClosesAt ?? record.windowClosesAt });
			if (session.alreadyStored) {
				// The server already has this recording (an earlier run finished but never registered).
				onProgress?.(100);
				patch({ state: 'uploaded', storagePublicId: session.storagePublicId, uploadedBytes: blob.size, attempts: 0 });
			}
		}

		if (!record.storagePublicId || record.state !== 'uploaded') {
			patch({ state: 'uploading', error: undefined, code: undefined });
			const out = await runRelayUpload(
				session,
				blob,
				{
					getSession: (uploadId) => deps.getSession(electionId, uploadId),
					uploadChunk: (s, b, index, p) => deps.uploadChunk(electionId, s, b, index, p),
					complete: (uploadId) => deps.complete(electionId, uploadId),
				},
				{
					// Persist after every acknowledged chunk; a reset of the attempt count means the
					// connection is working.
					onChunk: (uploadedBytes) => patch({ uploadedBytes, attempts: 0 }),
					onProgress,
				}
			);
			if (out.kind === 'done') {
				patch({ state: 'uploaded', storagePublicId: out.data.storagePublicId, uploadedBytes: blob.size, attempts: 0 });
			} else if (out.kind === 'restart') {
				// The session is dead (expired, unknown, bad range, hash mismatch): re-sign next attempt.
				return patch({ state: 'queued', session: undefined, uploadedBytes: 0, ...backoff(record, deps, { code: out.code }) });
			} else if (out.kind === 'refused') {
				return patch({ state: 'refused', code: out.code, error: refusalMessage(out.status, out.code) });
			} else {
				// offline / retry: keep the session; the server's receivedChunks is where it resumes.
				const code = out.kind === 'retry' ? out.code : undefined;
				const retryAfterMs = out.kind === 'retry' ? out.retryAfterMs : undefined;
				return patch({ state: 'uploading', ...backoff(record, deps, { code, retryAfterMs }) });
			}
		}
	}

	if (!record?.storagePublicId || record.state !== 'uploaded') return record;

	// ── 2. register with api.core ──
	const reg = await deps.register(electionId, {
		pollingUnitCode: record.pollingUnitCode,
		resultReferenceId: record.resultReferenceId,
		storagePublicId: record.storagePublicId,
		mime: record.mime,
		bytes: record.bytes,
		durationSec: record.durationSec,
		sha256: record.sha256,
		captureMode: record.captureMode,
		captureStartedAt: record.captureStartedAt,
		captureEndedAt: record.captureEndedAt,
		latitude: record.coords?.latitude,
		longitude: record.coords?.longitude,
		accuracyMeters: record.coords?.accuracy ?? undefined,
		chapters: record.chapters,
		deviceInfo: record.deviceInfo,
	});
	if (reg.kind === 'ok') {
		const done = patch({
			state: 'registered',
			registeredAt: reg.data.registeredAt,
			error: undefined,
			code: undefined,
			attempts: 0,
		});
		void deps.deleteBlob(id);
		return done;
	}
	if (isTerminal(reg)) {
		// The blob stays until the agent dismisses the card — it is their only copy.
		return patch({ state: 'refused', code: reg.code, error: refusalMessage(reg.status, reg.code, resultMessage(reg)) });
	}
	// 409 STORAGE_PENDING: the asset isn't confirmed in storage yet — back off and register again.
	const code = 'code' in reg ? reg.code : undefined;
	return patch({ state: 'uploaded', ...backoff(record, deps, { code }) });
};

/** App start / `online` / tick: push every record on the device that has something due. */
export const flushDueResultVideos = async (deps: IVideoSyncDeps = defaultVideoSyncDeps): Promise<void> => {
	const me = Number(getUserData()?.user?.UserId) || null;
	for (const record of listVideoRecords()) {
		// Records belong to whoever recorded them; another agent on a shared phone must not send them.
		if (me && record.userId && record.userId !== me) continue;
		await syncResultVideo(record.electionId, record.pollingUnitCode, { deps });
	}
};

/**
 * What the status card says about a record, in one place so every surface agrees. `syncing` is
 * whether a run is actually in flight in this tab: a record can sit in `uploading` while it waits
 * out a retry delay, and that must read as paused, not as progress.
 */
export const describeVideoRecord = (
	r: IResultVideoRecord,
	progress?: number,
	syncing = false
): { tone: 'working' | 'ok' | 'attention' | 'blocked'; text: string } => {
	const percent = progress ?? uploadedPercent(r);
	// The server can't store media right now: say so plainly, whatever the state, while no run is live.
	if (!syncing && isStoragePaused(r.code) && (r.state === 'queued' || r.state === 'uploading' || r.state === 'uploaded')) {
		return { tone: 'attention', text: VIDEO_COPY.storagePaused };
	}
	switch (r.state) {
		case 'registered':
			return { tone: 'ok', text: 'Video added to your result' };
		case 'uploaded':
			return { tone: 'working', text: 'Video uploaded — finishing up' };
		case 'uploading':
			return syncing
				? { tone: 'working', text: `Uploading video · ${percent}%` }
				: { tone: 'attention', text: `Upload paused at ${percent}% — it will continue automatically` };
		case 'queued':
			return r.wifiOnly
				? { tone: 'working', text: 'Video saved on this phone — uploading when you’re on Wi-Fi' }
				: { tone: 'working', text: 'Video saved on this phone — uploading when you have signal' };
		case 'held':
			return { tone: 'attention', text: r.error || VIDEO_COPY.held };
		case 'missing':
			return { tone: 'blocked', text: VIDEO_COPY.missing };
		case 'refused':
		default:
			return { tone: 'blocked', text: r.error || VIDEO_COPY.refused };
	}
};

export interface IQueueVideoInput {
	electionId: number;
	pollingUnitCode: string;
	pollingUnitName?: string;
	resultReferenceId: string;
	file: Blob;
	fileName: string;
	captureMode: IResultVideoRecord['captureMode'];
	durationSec?: number;
	captureStartedAt?: string;
	captureEndedAt?: string;
	coords?: IResultVideoRecord['coords'];
	chapters?: IResultVideoRecord['chapters'];
	deviceInfo?: string;
	windowClosesAt?: string;
	wifiOnly?: boolean;
}

/**
 * Saves a captured video to this phone FIRST (blob in IndexedDB, record in localStorage), then
 * starts uploading. Replaces any earlier local record for the unit — one current video per unit —
 * and frees that record's blob. The returned record is already durable when this resolves.
 */
export const queueResultVideo = async (
	input: IQueueVideoInput,
	{
		deps = defaultVideoSyncDeps,
		sha256,
		onProgress,
	}: { deps?: IVideoSyncDeps; sha256?: (blob: Blob) => Promise<string | undefined>; onProgress?: (p: number) => void } = {}
): Promise<IResultVideoRecord> => {
	const previous = getVideoRecord(input.electionId, input.pollingUnitCode);
	const id = `rv-${deps.now().toString(36)}-${Math.floor(deps.random() * 1e9).toString(36)}`;
	await putVideoBlob(id, input.file);
	const record = writeVideoRecord(
		newVideoRecord({
			id,
			electionId: input.electionId,
			userId: Number(getUserData()?.user?.UserId) || undefined,
			pollingUnitCode: input.pollingUnitCode,
			pollingUnitName: input.pollingUnitName,
			resultReferenceId: input.resultReferenceId,
			mime: (input.file.type || 'video/webm').split(';')[0],
			bytes: input.file.size,
			fileName: input.fileName,
			durationSec: input.durationSec,
			sha256: sha256 ? await sha256(input.file) : undefined,
			captureMode: input.captureMode,
			captureStartedAt: input.captureStartedAt,
			captureEndedAt: input.captureEndedAt,
			coords: input.coords,
			chapters: input.chapters ?? [],
			deviceInfo: input.deviceInfo,
			windowClosesAt: input.windowClosesAt,
			wifiOnly: input.wifiOnly,
			createdAt: deps.now(),
		})
	);
	if (previous && previous.id !== id) void deleteVideoBlob(previous.id);
	void syncResultVideo(input.electionId, input.pollingUnitCode, { force: true, deps, onProgress });
	return record;
};
